import type { Photo } from "@/core/photo"
import { clamp } from "@/core/utils/math"
import type { MaskCache, MaskArtifactDescriptor } from "@/storage/mask-cache"
import type {
	AIMaskArtifactChunkRecord,
	AIMaskArtifactKind,
} from "@/storage/ai-mask-artifact-types"
import type { vec2 } from "gl-matrix"

export interface MaskResult {
	mask: Float32Array
	width: number
	height: number
}

export class AIMaskNotDetectedError extends Error {}

export type RenderMaskAnalysisInput = (photo: Photo, size: vec2) => Promise<ImageBitmap | undefined>

export interface MaskArtifactDescriptorOptions {
	settingsDigest?: string
	parentBundleId?: string
	personIndex?: number
}

export function getArtifactDescriptor(
	cache: MaskCache | undefined,
	photo: Photo,
	kind: AIMaskArtifactKind,
	pipelineSignature: string,
	options: MaskArtifactDescriptorOptions = {},
) {
	return cache?.createArtifactDescriptor(photo, kind, pipelineSignature, options)
}

export async function writeArtifact(
	cache: MaskCache | undefined,
	descriptor: MaskArtifactDescriptor | undefined,
	metadata: Record<string, unknown>,
	chunks: AIMaskArtifactChunkRecord[],
) {
	if (!descriptor || !cache) {
		return false
	}
	return await cache.writeArtifactBundle(
		{
			...descriptor,
			metadata,
		},
		chunks,
	)
}

export function encodeAlphaMask(mask: Float32Array) {
	// Fresh inference uses the same 8-bit representation as cache hits so both paths render identically.
	const encoded = new Uint8Array(mask.length)
	for (let index = 0; index < mask.length; index += 1) {
		encoded[index] = Math.round(clamp(mask[index] ?? 0, 0, 1) * 255)
	}
	return encoded
}

export function decodeAlphaMask(chunk: AIMaskArtifactChunkRecord): MaskResult {
	const encoded = new Uint8Array(chunk.data)
	const mask = new Float32Array(encoded.length)
	for (let index = 0; index < encoded.length; index += 1) {
		mask[index] = (encoded[index] ?? 0) / 255
	}
	return { mask, width: chunk.width, height: chunk.height }
}

export function createArtifactChunk(
	bundleId: string,
	role: string,
	width: number,
	height: number,
	data: Uint8Array,
	encoding: AIMaskArtifactChunkRecord["encoding"],
): AIMaskArtifactChunkRecord {
	const stored = data.slice()
	return {
		id: `${bundleId}:${role}`,
		bundleId,
		role,
		width,
		height,
		encoding,
		data: stored.buffer,
		byteSize: stored.byteLength,
	}
}

// Converts an inference Float32 alpha mask into the canonical storage representation by encoding
// it as Uint8 and decoding it back. Returning the decoded result prevents fresh inference from
// having greater precision than a later IndexedDB cache hit.
export function canonicalizeAlphaMask(
	bundleId: string | undefined,
	role: string,
	result: MaskResult,
) {
	const encoded = encodeAlphaMask(result.mask)
	const chunk = createArtifactChunk(
		bundleId ?? `memory:${role}`,
		role,
		result.width,
		result.height,
		encoded,
		"u8-alpha-v1",
	)
	return { result: decodeAlphaMask(chunk), chunk }
}
